/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@i18n/react";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { useViewedScreen } from "@packages/analytics";
import { getFullName, useRequestMe, zodRequiredString } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { spacing } from "../../../../global/emotion";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps, AuthProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
};
const headerStyle: Interpolation = {
  height: spacing.xl,
  width: "100%",
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};

export const AuthNamesFlow = ({
  hideHeader,
  onAuth,
  className,
  setFlow,
  skipDOBCollection,
}: Omit<AuthFlowProps, "onAuth"> & Pick<AuthProps, "onAuth">) => {
  const { query: meQuery } = useRequestMe();
  const t = useTranslations();

  const authFlowNamesDataSchema = z.object({
    name: zodRequiredString(`*${t("web_input_error_required")}`).refine(
      // Require a first and last name: at least two space-separated parts, each
      // with at least one non-space character (e.g. "m m" is valid, "mm" isn't).
      (value) => value.trim().split(/\s+/).length >= 2,
      { message: `*${t("web_payments_full_name_incomplete")}` },
    ),
  });
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } = useValidator<
    z.infer<typeof authFlowNamesDataSchema>
  >(zodResolver(authFlowNamesDataSchema));
  const form = useForm({ resolver });
  const { flags } = useMixpanel();
  const isTargetedDOB = flags?.[HallowABFlag.WebDOBCollection]?.isTargeted;

  const { mutationPut } = useRequestMe();
  useViewedScreen({ screen: "name_question" });

  const onSubmit = async ({
    name,
  }: z.infer<typeof authFlowNamesDataSchema>) => {
    try {
      const [firstName, ...lastName] = name.trim().split(/\s+/);
      await mutationPut.mutateAsync({
        last_name: lastName.join(" "),
        name: firstName,
      });
    } catch (error) {}
    if (isTargetedDOB && !skipDOBCollection) setFlow("dob");
    else onAuth({ isNewUser: true });
  };

  return (
    <AuthForm<z.infer<typeof authFlowNamesDataSchema>>
      form={form}
      defaultValues={{
        name:
          getFullName({
            firstName: meQuery.data?.name,
            lastName: meQuery.data?.last_name,
          }) || "",
      }}
      onSubmit={onSubmit}
      footer={(_, disabled) => (
        <div css={buttonsStyle}>
          <AuthSubmitButton disabled={disabled}>
            {t("onboarding_button_continue")}
          </AuthSubmitButton>
        </div>
      )}
      className={className}
    >
      {(form) => (
        <>
          {!hideHeader && <div css={headerStyle} />}
          <Text type="headline" size="m" as="h2">
            {t("full_name_headline_new")}
          </Text>
          <FormField
            control={form.control}
            name="name"
            showErrorMessage={false}
            defaultValue={""}
            render={({ field }) => (
              <AuthTextInput
                required
                autoFocus
                autoComplete="name"
                autoCorrect="off"
                placeholder={t("full_name_input_hint")}
                css={fieldsCSS[field.name]}
                data-testid={HallowDataTestId.AuthFirstNameInput}
                {...inputProps}
                {...field}
              />
            )}
            css={inputFormFieldStyle}
          />
          <AuthError css={errors?.name?.message ? errorsCSS.name : null}>
            {errors?.name?.message ? t(errors.name.message) : ""}
          </AuthError>
        </>
      )}
    </AuthForm>
  );
};
